import React, { useCallback, useContext, useEffect, useState } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useUserId } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { formatISODate } from 'src/utils/dateUtils';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { presentationsDownloadServerProxy } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/service/api';
import {
  PresentationDownloadItem,
  PresentationDownloadStatus,
  PresentationsDownloadListResponse
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import { useAppSelector } from 'src/utils/Hooks';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { NotificationBadge } from 'src/components/common/NotificationBadge';
import { NotificationContext } from 'src/providers/NotificationProvider';
import { NotificationEvent } from 'src/hooks/useNotificationsSubscriber';

const PRESENTATION_DOWNLOAD_PAGE_SIZE = 20;
export const PRESENTATION_DOWNLOADS_QUERY_KEY = 'BULK_PRESENTATION_DOWNLOADS';

const PresentationStatusDisplayName: Record<PresentationDownloadStatus, string> = {
  COMPLETE: 'Download',
  FAILED: 'Failed',
  IN_QUEUE: 'Processing',
  PROCESSING: 'Processing',
  RETRYING: 'Retrying',
  DONE: 'Download',
  REQUESTED: 'Processing',
  IN_PROGRESS: 'Processing',
};

interface ServerProxy {
  fetchPresentationsList: (paginationToken: string) => Promise<PresentationsDownloadListResponse>;
}

const usePresentationsDownloadServerProxy = (): ServerProxy => {
  const pageSize = 20;

  const proxy = presentationsDownloadServerProxy(pageSize);

  return { fetchPresentationsList: proxy.fetchPresentationsList };
}

const DownloadCell = ({ value, row }: { value: PresentationDownloadStatus; row: PresentationDownloadItem }) => {
  const isSuccessful = value === 'COMPLETE' || value === 'DONE';
  const [shouldShowNotifications, setShouldShowNotifications] = useState(false);
  const { numberOfDownloads, requestId, source } = row;

  const { targetUrl } = useAppSelector((state) => state.app);

  const downloadEndpoint = source === 'v2' 
    ? `https://${targetUrl}/api/stories/v2/download?requestId=${requestId}`
    : `https://${targetUrl}/api/stories/download?requestId=${requestId}`;

  useEffect(() => {
    const downloaded = numberOfDownloads && numberOfDownloads !== 0;
    if (!downloaded && isSuccessful) {
      setShouldShowNotifications(true);
    }
  }, [isSuccessful, numberOfDownloads]);

  return (
    <Flex style={{ alignItems: 'center' }}>
      <a
        style={{
          textDecoration: isSuccessful ? '' : 'none',
          pointerEvents: isSuccessful ? 'auto' : 'none'
        }}
        onClick={() => {
          setShouldShowNotifications(false);
        }}
        href={downloadEndpoint}
      >
        <Text variant="body2">{PresentationStatusDisplayName[value]}</Text>
      </a>
      {shouldShowNotifications && (
        <NotificationBadge variant="small" />
      )}
    </Flex>
  );
};

const downloadPresentationsColumnDefinition = [
  {
    field: 'key',
    headerName: 'FILE NAME',
    tableColumnProps: {
      style: {
        width: '530px'
      }
    },
    valueFormatter: (value: string | undefined, row: PresentationDownloadItem) => {
      const displayValue = value || `reports/${row.requestId}.pptx`;
      const fileNameSplit = displayValue.split('/');
      const fileNameWithoutExtension = fileNameSplit[fileNameSplit.length - 1].replace('.pptx', '');
      return (
        <Flex paddingLeft="8px">
          <Text variant="body2">{fileNameWithoutExtension}</Text>
        </Flex>
      );
    }
  },
  {
    field: 'createdAt',
    headerName: 'DATE',
    tableColumnProps: {
      style: {
        width: '221px'
      }
    },
    valueFormatter: (value: string) => {
      return (
        <Flex>
          <div>
            <Text variant="body2">{formatISODate(value, 'M/D/YY')}</Text>
          </div>
        </Flex>
      );
    }
  },
  {
    field: 'status',
    headerName: 'STATUS',
    valueFormatter: (value: PresentationDownloadStatus, row: PresentationDownloadItem) => {
      return <DownloadCell row={row} value={value} />;
    }
  }
];

/**
 * In settings page, there is a download menu, inside of the menu we have tab - "Export"  and "Presentations"
 * This table is for the presentation download
 */
export const PresentationDownloadTable = () => {
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState<number | null>(null);
  const [pageToToken, setPageToToken] = useState<Record<number, string | null>>({ 1: null });
  const { setPresentationBadge } = useNotificationsBadge();
  const queryClient = useQueryClient();
  const COL_DEFS = downloadPresentationsColumnDefinition;

  const userId = useUserId();
  const { fetchPresentationsList } = usePresentationsDownloadServerProxy();

  const { setDataFormatters } = useContext(NotificationContext);

  const { data: presentationsTemplateList, isLoading } = useQuery<PresentationDownloadItem[]>({
    queryKey: [PRESENTATION_DOWNLOADS_QUERY_KEY, pageToToken[page]],
    queryFn: async () => {
      try {
        const response = await fetchPresentationsList(pageToToken[page]);

        const { result: presentationList, paginationToken } = response;
        setPageToToken({ ...pageToToken, [page + 1]: paginationToken });
        if (!paginationToken) {
          setMaxPage(page);
        }

        return presentationList;
      } catch (error) {
        console.error('Error:: Fetching initial data for PRESENTATION DOWNLOAD SETTING for user - ', userId, error);
      }
      return [];
    },
    retry: 0
  });

  const onNextPage = () => {
    if (isLoading) {
      return;
    }

    if (maxPage) {
      setPage(Math.min(page + 1, maxPage));
    } else {
      setPage(page + 1);
    }
  };

  const onPreviousPage = () => {
    setPage(Math.max(page - 1, 1));
  };

  const handlePresentationsEvent = useCallback((event: NotificationEvent) => {
    // Find which page contains this requestId and invalidate that page
    Object.entries(pageToToken).forEach(([pageNum, token]) => {
      const queryKey = [PRESENTATION_DOWNLOADS_QUERY_KEY, token];
      const existingData = queryClient.getQueryData<PresentationDownloadItem[]>(queryKey);

      if (existingData && Array.isArray(existingData)) {
        const hasRequestId = existingData.some((item) => item.requestId === event.requestId);
        if (hasRequestId) {
          queryClient.invalidateQueries({ queryKey });
        }
      }
    });

    setPresentationBadge(false);
  }, [pageToToken, queryClient, setPresentationBadge]);

  useEffect(() => {
    return () => {
      // We will lose the pagination tokens so clear the cache on unmount
      queryClient.removeQueries({ queryKey: [PRESENTATION_DOWNLOADS_QUERY_KEY] });
    };
  }, [queryClient]);

  useEffect(() => {
    setPresentationBadge(false);
    setDataFormatters((prev) => ({
      ...prev,
      presentations: handlePresentationsEvent
    }));

    return () => {
      setDataFormatters((prev) => ({
        ...prev,
        presentations: (event) => {}
      }));
    };
  }, [setPresentationBadge, setDataFormatters, handlePresentationsEvent]);

  return (
    <>
      <Flex flexDirection="column">
        <EntityTable
          isLoading={isLoading}
          rowData={presentationsTemplateList ? [...presentationsTemplateList] : []}
          columnDefs={COL_DEFS}
          shouldModifyColumnDefs={false}
          initialPageSize={PRESENTATION_DOWNLOAD_PAGE_SIZE}
          showPagination={false}
          skeletonRows={PRESENTATION_DOWNLOAD_PAGE_SIZE}
          appTableProps={{
            noDataPlaceholder: (
              <NoDataPlaceHolderTableRow
                title="No presentations yet"
                description="After generating presentation from your account presentation builder, visit this page to access them."
              />
            )
          }}
        />
        <Flex justifyContent="flex-end">
          <Pagination
            currentPage={page}
            onClickNext={onNextPage}
            onClickPrevious={onPreviousPage}
            shouldUseInfinitePagination
          />
        </Flex>
      </Flex>
    </>
  );
};

export default PresentationDownloadTable;
